const CONFIG_SELECTOR = '[data-gok-market-geolocation]';
const SESSION_KEY = 'gok-market-geolocation-v1';

/**
 * Normalizes an ISO 3166-1 alpha-2 country code.
 *
 * @param {unknown} value
 * @returns {string}
 */
function normalizeCountryCode(value) {
  return typeof value === 'string' ? value.trim().toUpperCase() : '';
}

/**
 * Reads the list of Shopify Markets countries rendered by Liquid.
 *
 * @param {string | undefined} value
 * @returns {Set<string>}
 */
function parseAvailableCountries(value) {
  try {
    const countries = JSON.parse(value || '[]');

    if (!Array.isArray(countries)) return new Set();

    return new Set(countries.map(normalizeCountryCode).filter(Boolean));
  } catch {
    return new Set();
  }
}

/**
 * Uses the detected country only when Shopify Markets supports it.
 *
 * @param {Set<string>} availableCountries
 * @param {string} detectedCountry
 * @param {string} fallbackCountry
 * @returns {string}
 */
function resolveTargetCountry(availableCountries, detectedCountry, fallbackCountry) {
  return availableCountries.has(detectedCountry) ? detectedCountry : fallbackCountry;
}

/**
 * @returns {boolean}
 */
function localizationWasResolved() {
  try {
    return sessionStorage.getItem(SESSION_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * @param {string} countryCode
 */
function markLocalizationAsResolved(countryCode) {
  try {
    sessionStorage.setItem(SESSION_KEY, countryCode);
  } catch {
    // Storage can be unavailable in privacy-restricted browsers.
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {HTMLInputElement} countryInput
 * @param {string} activeCountry
 * @param {string} countryCode
 */
function applyCountry(form, countryInput, activeCountry, countryCode) {
  markLocalizationAsResolved(countryCode);

  if (countryCode === activeCountry) return;

  countryInput.value = countryCode;
  form.submit();
}

async function initializeCountryFromIp() {
  const config = document.querySelector(CONFIG_SELECTOR);

  if (!(config instanceof HTMLElement) || localizationWasResolved()) return;
  if (document.documentElement.classList.contains('shopify-design-mode')) return;

  const form = config.querySelector('form');
  const countryInput = form?.querySelector('input[name="country_code"]');

  if (!(form instanceof HTMLFormElement) || !(countryInput instanceof HTMLInputElement)) return;

  const availableCountries = parseAvailableCountries(config.dataset.availableCountries);
  const activeCountry = normalizeCountryCode(config.dataset.activeCountry);
  const fallbackCountry = normalizeCountryCode(config.dataset.fallbackCountry);

  if (availableCountries.size === 0 || !availableCountries.has(fallbackCountry)) return;

  let detectedCountry = '';

  try {
    const root = window.Shopify?.routes?.root || '/';
    const suggestionsUrl = new URL(`${root}browsing_context_suggestions.json`, window.location.origin);
    suggestionsUrl.searchParams.set('country[enabled]', 'true');

    const response = await fetch(suggestionsUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error(`Country detection failed with status ${response.status}`);

    const browsingContext = await response.json();
    detectedCountry = normalizeCountryCode(browsingContext?.detected_values?.country?.handle);
  } catch {
    // If Shopify can't determine the visitor's country, use the configured fallback.
  }

  const targetCountry = resolveTargetCountry(availableCountries, detectedCountry, fallbackCountry);
  applyCountry(form, countryInput, activeCountry, targetCountry);
}

initializeCountryFromIp();
